import { Modal } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { useState, useEffect, useRef } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'utils/pdfWorker';
import './index.less';

export interface RevxPDFPreviewModalProps {
  visible: boolean;
  frontPdfUrl?: string;
  frontPdfName?: string;
  backPdfUrl?: string;
  backPdfName?: string;
  backImageUrl?: string;
  loading?: boolean;
  onApprove: () => void;
  onCancel: () => void;
  cancelText?: string;
}

export const RevxPDFPreviewModal = ({
  visible,
  frontPdfUrl,
  frontPdfName,
  backPdfUrl,
  backPdfName,
  backImageUrl,
  loading = false,
  onApprove,
  onCancel,
  cancelText = 'Cancel',
}: RevxPDFPreviewModalProps) => {
  const [approving, setApproving] = useState(false);
  const isMountedRef = useRef(true);

  // 组件卸载时标记
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // 当 modal 关闭时重置 approving 状态
  useEffect(() => {
    if (!visible && isMountedRef.current) {
      console.log('Modal: visible changed to false, resetting approving');
      setApproving(false);
    }
  }, [visible]);

  const handleApprove = async () => {
    console.log('Modal: handleApprove started, setting approving to true');
    setApproving(true);
    try {
      await onApprove();
      console.log('Modal: onApprove completed successfully');
      // 只在组件仍然挂载时更新状态
      if (isMountedRef.current) {
        setApproving(false);
      }
    } catch (error) {
      console.log('Modal: onApprove failed');
      if (isMountedRef.current) {
        setApproving(false);
      }
      throw error;
    }
  };

  const handleDownload = (url: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'download.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal
      title="PDF Preview"
      visible={visible}
      onCancel={onCancel}
      width={1200}
      footer={null}
      className="revx-pdf-preview-modal"
      destroyOnClose
    >
      <RevxSpin spinning={loading || approving}>
        <div className="pdf-preview-container">
          {/* Front PDF */}
          {frontPdfUrl && (
            <div className="pdf-preview-section">
              <h3 className="pdf-preview-title">Front</h3>
              <div className="pdf-preview-wrapper">
                <RevxButton
                  className="pdf-download-btn"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() =>
                    handleDownload(frontPdfUrl, frontPdfName || 'front.pdf')
                  }
                />
                <Document
                  file={frontPdfUrl}
                  loading={<div className="pdf-loading">Loading ...</div>}
                  error={<div className="pdf-error">Failed to load PDF</div>}
                >
                  <Page
                    pageNumber={1}
                    width={550}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Document>
              </div>
            </div>
          )}

          {/* Back PDF */}
          {(backPdfUrl || backImageUrl) && (
            <div className="pdf-preview-section">
              <h3 className="pdf-preview-title">Back</h3>
              <div className="pdf-preview-wrapper">
                {backPdfUrl && (
                  <RevxButton
                    className="pdf-download-btn"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() =>
                      handleDownload(backPdfUrl!, backPdfName || 'back.pdf')
                    }
                  />
                )}
                {backImageUrl ? (
                  <img
                    src={backImageUrl}
                    alt="Back Preview"
                    style={{ width: 550, objectFit: 'contain' }}
                  />
                ) : (
                  <Document
                    file={backPdfUrl!}
                    loading={<div className="pdf-loading">Loading ...</div>}
                    error={<div className="pdf-error">Failed to load PDF</div>}
                  >
                    <Page
                      pageNumber={1}
                      width={550}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer with Approve button */}
        <div className="pdf-preview-footer">
          <RevxButton onClick={onCancel} style={{ marginRight: 12 }}>
            {cancelText}
          </RevxButton>
          <RevxButton
            type="primary"
            brandGreen
            onClick={handleApprove}
            loading={approving}
          >
            Approve
          </RevxButton>
        </div>
      </RevxSpin>
    </Modal>
  );
};

export default RevxPDFPreviewModal;
